// The conversation and the sheets, shared by both concepts.
import { useEffect, useRef, useState } from 'react';
import { Renderer, type ActionEvent } from '@openuidev/react-lang';
import { library, RecordsContext } from '@roundstead/shared/library';
import { useStore } from './store.tsx';
import { Button, Bracket, Composer, KV, Sheet } from './ds.tsx';
import { OfferPrices, RoutineHelp, T, TermsRows, UnresolvedList } from './cards.tsx';
import { W } from './words.ts';
import { CORE_CARD, view } from '../../content/content.ts';

/** The busy line says honestly when a reply is slow; progress is kept either way. */
function Busy({ text }: { text: string }) {
  const [t, setT] = useState(0);
  useEffect(() => { const a = setTimeout(() => setT(1), 15000); const b = setTimeout(() => setT(2), 45000); return () => { clearTimeout(a); clearTimeout(b); }; }, []);
  return <div className="rs-ai rs-in"><span className="hc-caption">{W.assistantLabel}</span><div className="hc-bubble hc-bubble--steward rs-shimmer">{t === 2 ? W.slow2 : t === 1 ? W.slow1 : text}</div></div>;
}

/** "Yes" alone on a review: the screen's own answer (F1 View 5), pointing at the exact purchase control. */
function YesReply() {
  const s = useStore();
  const toAccept = () => { s.setSheet(null); s.openAccept(); };
  return (
    <div className="rs-ai rs-in"><span className="hc-caption">{W.assistantLabel}</span>
      <div className="hc-bubble hc-bubble--steward">{view(1, 5).assistant} {W.yesWhich}</div>
      <div className="rs-pills">
        <Button className="rs-pill" onClick={toAccept}>{W.openAccept}</Button>
        <Button className="rs-pill" onClick={() => (document.querySelector('.rs-sheet--open .hc-composer__input, .rs-dock .hc-composer__input') as HTMLInputElement | null)?.focus()}>{W.meantElse}</Button>
        <Button className="rs-pill" onClick={() => s.contact()}>{W.contactBob}</Button>
      </div>
    </div>
  );
}

export function Thread({ onAction, follow }: { onAction: (a: ActionEvent) => void; follow?: boolean }) {
  const { entries, records, busy } = useStore();
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => { if (follow) end.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [follow, entries.length, busy]);
  if (!records) return null;
  return (
    <RecordsContext.Provider value={records}>
      <div className="rs-thread" role="log" aria-label="Conversation">
        {entries.map((e) => e.from === 'home'
          ? <div key={e.id} className="hc-bubble hc-bubble--home rs-in">{e.text}</div>
          : e.from === 'local' ? <YesReply key={e.id} />
          : <div key={e.id} className="rs-ai rs-in"><span className="hc-caption">{W.assistantLabel}</span><Renderer response={e.reply} library={library} onAction={onAction} />
              {e.fallback && <span className="hc-caption">{W.checkedOut}</span>}</div>)}
        {busy && <Busy text={busy} />}
        <div ref={end} />
      </div>
    </RecordsContext.Provider>
  );
}

/** Every sheet, for both concepts. */
export function Sheets({ onAction }: { onAction: (a: ActionEvent) => void }) {
  const s = useStore();
  const close = () => s.setSheet(null);
  return (
    <>
      <Sheet open={s.sheet === 'ask'} title={W.askAssistant} onClose={close}
             footer={<Composer onSend={(t) => s.turn({ text: t }, t)} disabled={!!s.busy} autoFocus={!s.entries.length} />}>
        {!s.entries.length && <p className="hc-caption">Ask about anything on this screen. Replying here never charges, books or changes anything.</p>}
        {s.scenario.assistant === 'scripted' && <p className="hc-caption">{W.scripted}</p>}
        <Thread onAction={onAction} follow />
        <div className="rs-row-actions"><Button kind="secondary" className="rs-auto" onClick={() => s.contact()}>{W.contactBob}</Button></div>
      </Sheet>
      <Sheet open={s.sheet === 'membership'} title={W.membership} onClose={close}>
        <p className="rs-m0">{CORE_CARD}</p>
        <RoutineHelp />
        <KV rows={[[W.term, <T>{W.termValue}</T>], [W.benefits, <T>{W.benefitsValue}</T>], ['Visits included', 'None. Each visit is separately purchased.'], ['Emergency service or monitoring', 'Not included']]} />
      </Sheet>
      <Sheet open={s.sheet === 'visits'} title={W.visitPrices} onClose={close}>
        <OfferPrices />
        <p className="hc-caption rs-m0">Members choose when to request a visit, and may request another whenever they wish. Actual windows need confirmation; no slot or immediate attendance is promised. Four or more units, or an unknown count, need a scoped quote.</p>
      </Sheet>
      <Sheet open={s.sheet === 'unresolved'} title={W.notDecided} onClose={close}><UnresolvedList /></Sheet>
      <Sheet open={s.sheet === 'terms'} title={W.scopeTerms} onClose={close}><TermsRows /><p className="hc-caption">These terms are unresolved, so this prototype cannot accept a real purchase.</p></Sheet>
      <Sheet open={s.sheet === 'route'} title={s.route?.title ?? ''} onClose={close}><p>{s.route?.value.startsWith('[') ? <Bracket>{s.route.value}</Bracket> : s.route?.value}</p></Sheet>
    </>
  );
}

/** What OpenUI buttons inside assistant replies do, the same in both concepts. */
export function useReplyActions(picker: { open: () => void }) {
  const s = useStore();
  return (a: ActionEvent) => {
    if (s.busy) return;
    if (a.type === 'add_photos') picker.open();
    else if (a.type === 'cant_get_photo') s.openExc('photo-skipped');
    else if (a.type === 'edit_units') { s.setSheet(null); if (s.h?.membership.state === 'active') s.openPage('records'); else s.setView(2); }
    else if (a.type === 'contact_bob') s.contact();
  };
}
